import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { SEARCH_THRESHOLD, type Option } from './Select';

/**
 * The same picker, for several answers.
 *
 * Deliberately the sibling of `Select` rather than a mode of it: the two share a look and a
 * keyboard, and almost nothing else. A single select closes on choice and a multi does not; one
 * shows a value and the other shows a row of chips that can overflow; one has an empty option and
 * the other has "clear". Folding them together produced a component that was mostly branches.
 *
 * The trigger is a `div` carrying `role="combobox"`, not a `button`. The "+N more" control lives
 * inside the field where the chips are, and a button inside a button is invalid HTML — the inner
 * one is unreachable by keyboard and ignored by assistive tech. A div with `tabindex="0"` and the
 * combobox role is valid ARIA and can legally contain one.
 */

/** How many chips fit in the field before the rest become a count. */
const VISIBLE_CHIPS = 3;

export function MultiSelect<T>({
  options,
  value,
  onChange,
  placeholder = 'None',
  renderOption,
  renderChip,
  ariaLabel,
  compact = false,
  dialogTitle,
  id,
}: {
  options: Option<T>[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  renderOption?: (option: Option<T>) => ReactNode;
  /** How a chosen one looks in the field. Defaults to its label. */
  renderChip?: (option: Option<T>) => ReactNode;
  ariaLabel?: string;
  /**
   * A smaller trigger, sized to its content rather than filling its container.
   *
   * The *popup* stays full size deliberately — a compact list is simply a harder-to-read list, and
   * the trigger is the only part competing for room in a facet row. Still 32px tall, so still
   * clear of the 24×24 floor.
   */
  compact?: boolean;
  dialogTitle?: string;
  id?: string;
}) {
  const auto = useId();
  const base = id ?? auto;
  const [open, setOpen] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const dialogSearch = useRef<HTMLInputElement>(null);

  const withSearch = options.length > SEARCH_THRESHOLD;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === '' ? options : options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const chosen = options.filter((o) => value.includes(o.value));

  useEffect(() => {
    setActive((a) => Math.min(Math.max(a, 0), Math.max(matches.length - 1, 0)));
  }, [matches.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (overflow) window.setTimeout(() => dialogSearch.current?.focus(), 0);
  }, [overflow]);

  const openWith = (next: boolean) => {
    setOpen(next);
    if (next) {
      setQuery('');
      setActive(0);
      window.setTimeout(() => search.current?.focus(), 0);
    } else {
      trigger.current?.focus();
    }
  };

  /** Toggling never closes the popup — the whole point is choosing several without reopening. */
  const toggle = (option: Option<T>) => {
    if (option.disabled) return;
    onChange(
      value.includes(option.value)
        ? value.filter((v) => v !== option.value)
        : [...value, option.value],
    );
  };

  /**
   * @param typing Whether the key landed in the filter field rather than on the trigger or the list.
   *
   * The filter input shared this handler with the trigger, and this picker binds **Space** to
   * "toggle the active option" — so typing a query with a space in it selected the first match
   * instead of typing a space, and a two-word label could not be searched for at all. `Home` and
   * `End` had the same shape: they moved the active option instead of the caret.
   *
   * `Select` does not have this bug, because it has no Space binding — which is why the fix is
   * here rather than shared. Arrows still move the active option in the field (a single-line input
   * does not use them), Enter still chooses, and Escape still closes.
   */
  const onKey = (e: React.KeyboardEvent, typing = false) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openWith(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      // Handled here, so it stops here — the same reason as `Select`: this picker sits inside the
      // filter panel, which also closes on Escape, and one keystroke should close one thing.
      e.stopPropagation();
      return openWith(false);
    }
    if (e.key === 'Tab') return setOpen(false);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      return setActive((a) => Math.min(a + 1, matches.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      return setActive((a) => Math.max(a - 1, 0));
    }
    if (e.key === 'Home' && !typing) {
      e.preventDefault();
      return setActive(0);
    }
    if (e.key === 'End' && !typing) {
      e.preventDefault();
      return setActive(matches.length - 1);
    }
    if (e.key === 'Enter' || (e.key === ' ' && !typing)) {
      e.preventDefault();
      const hit = matches[active];
      if (hit) toggle(hit);
    }
  };

  const list = (inDialog: boolean) => (
    <ul
      className="sel-list"
      id={inDialog ? `${base}-dlg-list` : `${base}-list`}
      role="listbox"
      aria-multiselectable="true"
    >
      {matches.map((option, index) => (
        <li
          key={option.value}
          id={inDialog ? undefined : `${base}-o${index}`}
          role="option"
          aria-selected={value.includes(option.value)}
          data-depth={option.depth ?? 0}
          className={`sel-opt ${!inDialog && index === active ? 'is-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            toggle(option);
          }}
          onMouseEnter={() => !inDialog && setActive(index)}
        >
          {renderOption ? renderOption(option) : <span className="t">{option.label}</span>}
        </li>
      ))}
      {matches.length === 0 && <li className="sel-empty">Nothing matches “{query}”.</li>}
    </ul>
  );

  const chip = (option: Option<T>) => (
    <span className="chip" key={option.value}>
      {renderChip ? renderChip(option) : option.label}
    </span>
  );

  return (
    <div className={`sel ${compact ? 'is-compact' : ''}`} ref={root}>
      <div
        ref={trigger}
        id={`${base}-btn`}
        className="sel-button ms-button"
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${base}-list`}
        {...(open && matches[active] ? { 'aria-activedescendant': `${base}-o${active}` } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        onKeyDown={onKey}
        onClick={(e) => {
          // The overflow control is inside the field, so its click must not also toggle the popup.
          if ((e.target as HTMLElement).closest('[data-overflow]')) return;
          openWith(!open);
        }}
      >
        <span className={`ms-values ${chosen.length === 0 ? 'is-empty' : ''}`}>
          {chosen.length === 0 ? (
            <span className="t">{placeholder}</span>
          ) : (
            <>
              {chosen.slice(0, VISIBLE_CHIPS).map(chip)}
              {chosen.length > VISIBLE_CHIPS && (
                <button
                  type="button"
                  className="ms-more"
                  data-overflow="1"
                  onClick={() => setOverflow(true)}
                >
                  +{chosen.length - VISIBLE_CHIPS} more
                </button>
              )}
            </>
          )}
        </span>
        <span className="sel-caret">
          <Icon name="caret" size={14} />
        </span>
      </div>

      <div className="sel-pop" hidden={!open}>
        {withSearch && (
          <div className="sel-search">
            <input
              ref={search}
              type="search"
              value={query}
              placeholder="Search"
              aria-label="Search options"
              onKeyDown={(e) => onKey(e, true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
            />
          </div>
        )}
        {list(false)}
      </div>

      {/*
        The overflow dialog.

        Not a nicety: past a handful, a chip row wraps to five lines on a phone and the field stops
        being a field. This is the same list with the same search, at a size where thirty chosen
        things are still manageable.
      */}
      {overflow && (
        <div
          className="sheet-scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOverflow(false);
          }}
        >
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={dialogTitle ?? 'Choose'}
          >
            <div className="sheet-head">
              <h4>{dialogTitle ?? ariaLabel ?? 'Choose'}</h4>
              <button type="button" className="link" onClick={() => setOverflow(false)}>
                Close
              </button>
            </div>
            <div className="sel-search">
              <input
                ref={dialogSearch}
                type="search"
                value={query}
                placeholder="Search"
                aria-label="Search options"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOverflow(false);
                }}
              />
            </div>
            <div className="sheet-body">{list(true)}</div>
            <div className="sheet-foot">
              <button type="button" className="btn" onClick={() => onChange([])}>
                Clear all
              </button>
              <button type="button" className="btn primary" onClick={() => setOverflow(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
